import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../elements/Layout';
import logo from '../../assets/miles_logo.svg';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';
import { IoCallSharp } from 'react-icons/io5';
import { MdOutlineMailOutline } from 'react-icons/md';
import { IoIosGlobe } from 'react-icons/io';
import { FaDownload, FaLocationDot } from 'react-icons/fa6';
import { useParams } from 'react-router-dom/dist';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import toast from 'react-hot-toast';
import { checkPaymentStatus } from '../../utils/api/api';
import ReactToPrint from 'react-to-print';
import Loading from '../../elements/Loading';
import moment from 'moment';
import { ToWords } from 'to-words';
import html2pdf from 'html2pdf.js';

const Receipt = () => {
    const [payment, setPayment] = useState([]);
    const [amountInWords, setAmountInWords] = useState('');
    const [loading, setLoading] = useState(false);
    const { id } = useParams();
    const toWords = new ToWords({
        localeCode: 'en-GB',
    });
    const componentRef = useRef();

    const getPayment = async () => {
        setLoading(true);
        try {
            const response = await checkPaymentStatus(id);
            setPayment(response.payment);
        } catch (error) {
            toast.error(error.message || "Failed to get transaction");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPayment();
    }, []);

    useEffect(() => {
        if (payment?.amount) {
            const words = toWords.convert(payment.amount);
            setAmountInWords(words);
        }
    }, [payment?.amount]);

    return (
        <Layout>
            {loading && <Loading />}
            <div className="w-full items-end flex justify-end gap-4 mb-4">
                <ReactToPrint
                    trigger={() => (
                        <button
                            className="px-6 py-2.5 text-white bg-gradient-to-r from-secondary to-secondary/90 rounded-full text-[14px] flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all"
                        >
                            <FaDownload />
                            Download Receipt
                        </button>
                    )}
                    content={() => componentRef.current}
                    documentTitle={`receipt_${payment?.transaction_id || 'download'}`}
                    pageStyle={`
                    @page {
                        size: A4 portrait;
                        margin: 10mm;
                    }
                    
                    @media print {
                        html, body {
                            width: 210mm;
                            height: 297mm;
                            margin: 0;
                            padding: 0;
                        }
                        
                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        
                        .receipt-container {
                            width: 100%;
                            height: 100%;
                            page-break-after: avoid;
                            page-break-inside: avoid;
                            transform-origin: top left;
                            transform: scale(0.95);
                        }
                    }
                `}
                />
            </div>
            {!loading && (
                <div
                    ref={componentRef}
                    className="bg-white max-w-6xl p-5 rounded-2xl mx-auto shadow border border-gray-100"
                    style={{
                        minHeight: '1000px',
                        paddingBottom: '4rem',
                        backgroundColor: 'white',
                    }}
              >
                    <div className="bg-gradient-to-r from-gray-50 to-white flex items-center justify-between p-5 border-[3px] border-primary rounded-2xl mb-4 shadow-sm">
                        <div className="flex items-center gap-4">
                            <img src={logo} className='h-auto w-[100px]' alt="Miles Logo" />
                            <div className="flex flex-col">
                                <p className="uppercase text-primary font-bold text-[16px] tracking-wide">Milescoop Ventures Ltd</p>
                                <p className="text-gray-600 text-[12px] uppercase">P.O. Box. 2760 - 00100 Nairobi</p>
                                <p className="text-gray-600 text-[12px]">Phone: +254 (0) 740 173 038</p>
                                <p className="text-gray-600 text-[12px]">Email: info@milescoop.com</p>
                                <p className="text-gray-600 text-[12px]">PIN NO: 052203487E</p>
                                <p className="text-gray-600 text-[12px]">Website: www.milescoop.com</p>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="bg-primary text-white py-1.5 rounded-t-xl">
                                <p className="px-6 uppercase font-bold text-[18px] text-center">Payment Receipt</p>
                            </div>
                            <div className="bg-white border-x-2 border-primary">
                                <div className="flex items-center justify-between w-full p-2 border-b border-gray-200">
                                    <p className="font-medium text-gray-700 text-[13px]">Date</p>
                                    <p className="text-gray-800 text-[13px]">{moment(payment?.createdAt).format("MMM DD, YYYY")}</p>
                                </div>
                                <div className="flex items-center justify-between w-full p-2 border-b border-gray-200">
                                    <p className="font-medium text-gray-700 text-[13px]">TRX ID</p>
                                    <p className="text-gray-800 font-mono text-[13px]">{payment.transaction_id}</p>
                                </div>
                                <div className="flex items-center justify-between w-full p-2">
                                    <p className="font-medium text-gray-700 text-[13px]">Status</p>
                                    <div className={`font-medium text-[14px] inline-block capitalize rounded-full px-4 py-1 text-white text-center h-[28px] leading-[18px] ${payment.status === 'completed' ? 'bg-green-600' : payment.status === 'pending' ? 'bg-yellow-600' : 'text-red-600'}`}>
                                        {payment.status}
                                    </div>
                                </div>
                            </div>
                            <div className="h-[4px] bg-primary rounded-b-xl"></div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl">
                        <div className="flex items-center">
                            <p className="w-[130px] text-gray-700 font-medium text-[14px]">
                                Received from:
                            </p>
                            <p className="font-bold uppercase text-primary text-[16px]">{payment?.customer?.first_name} {payment?.customer?.last_name}</p>
                        </div>

                        <div className="flex items-center w-full justify-between mt-3">
                            <div className="flex items-center">
                                <p className="w-[130px] text-gray-700 font-medium text-[14px]">
                                    Currency:
                                </p>
                                <div className="font-medium uppercase text-gray-700 text-[14px] bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm">KES</div>
                            </div>
                            <div className="flex items-center">
                                <p className="w-[80px] text-gray-700 font-medium text-[14px]">
                                    Amount:
                                </p>
                                <div className="font-bold text-primary text-[16px] bg-white border border-gray-200 rounded-xl px-5 py-1.5 shadow-sm">
                                    {Number(payment?.amount).toLocaleString("KES")}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center mt-3">
                            <p className="w-[130px] text-gray-700 font-medium text-[14px]">
                                Amt in words:
                            </p>
                            <p className="w-full text-end font-medium text-gray-700 text-[14px] bg-white border border-gray-200 rounded-xl px-4 py-1.5 shadow-sm">
                                {amountInWords} shillings only
                            </p>
                        </div>
                    </div>

                    <div className="bg-white mt-4 p-2 flex flex-col rounded-md border border-primary">
                        <p className="text-primary text-[15px] font-bold mb-1">Remarks</p>
                        <p className="text-gray-700 text-[13px] leading-relaxed">
                            Amount KES {Number(payment?.amount).toLocaleString("KES")} received from
                            <span className="px-1 text-primary font-semibold uppercase">{payment?.customer?.first_name} {payment?.customer?.last_name}</span>
                            transaction reference number
                            <span className="px-1 text-primary font-semibold">{payment.transaction_id}</span>
                            dated
                            <span className="px-1 text-primary font-semibold">{moment(payment?.createdAt).format("MMM DD, YYYY, HH:mm A")}</span>
                        </p>
                    </div>

                    <div className="bg-white mt-4 p-2 flex flex-col rounded-md border border-primary">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                                <div className="h-1.5 w-1.5 bg-white rounded-full"></div>
                            </div>
                            <p className="text-primary text-[15px] font-bold">Payment Method: {payment?.method}</p>
                        </div>
                        <div className="flex items-center justify-between w-full text-[13px] text-gray-700">
                            <p className="flex items-center gap-1">
                                <span className="font-medium">Transaction ID:</span>
                                <span className="font-mono">{payment.transaction_id}</span>
                            </p>
                            <p className="">
                                <span className="font-medium">Date & time:</span>
                                {moment(payment?.createdAt).format("MMM DD, YYYY, HH:mm A")}
                            </p>
                        </div>
                    </div>

                    {payment.serial_no && (
                        <div className="flex items-center w-full mt-4 justify-between px-[15%] py-3 bg-white rounded-xl border border-gray-100">
                            <Barcode
                                value={payment?.serial_no}
                                width={1.5}
                                height={60}
                                displayValue={true}
                                background="#ffffff"
                                lineColor="#000000"
                                margin={5}
                                fontSize={12}
                                fontOptions="monospace"
                                textAlign="center"
                            />
                            <div className="p-1.5 bg-white rounded-xl shadow-sm">
                                <QRCode
                                    value={payment?.serial_no}
                                    size={70}
                                    bgColor="#ffffff"
                                    fgColor="#000000"
                                />
                            </div>
                        </div>
                    )}

                    <footer className="mt-4 bg-gray-50 rounded-xl p-4">
                        <div className="flex justify-between text-gray-600 text-[13px]">
                            <div className="flex flex-col gap-2">
                                <p className="flex items-center gap-2">
                                    <span className="bg-secondary text-white rounded-full p-2 shadow-md"><IoCallSharp size={14} /></span>
                                    +254 (0) 737 900 424
                                </p>
                                <p className="flex items-center gap-2">
                                    <span className="bg-secondary text-white rounded-full p-2 shadow-md"><MdOutlineMailOutline size={14} /></span>
                                    info@milescoop.com
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="flex items-center gap-2">
                                    <span className="bg-secondary text-white rounded-full p-2 shadow-md"><IoIosGlobe size={14} /></span>
                                    www.milescoop.com
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="flex items-center gap-2">
                                    <span className="bg-secondary text-white rounded-full p-2 shadow-md"><FaLocationDot size={14} /></span>
                                    Design Centre, 4th floor, Room 4A
                                </p>
                                <p className="flex items-center gap-2">
                                    <span className="bg-secondary text-white rounded-full p-2 shadow-md"><FaLocationDot size={14} /></span>
                                    PIN NUMBER: 052203487E
                                </p>
                            </div>
                        </div>
                    </footer>

                    <div className="mt-4 p-2 rounded-lg border border-primary">
                        <p className="font-medium text-black text-[15px]">T&Cs Apply</p>
                        <p className='text-gray-700 leading-relaxed'>
                            <span className='text-black font-medium text-[15px]'>NOTE: This document is computer generated. It is a valid document issued under the authority of Milescoop Ventures Limited. </span>
                            <span className="text-[13px]">
                                All payments made towards the purchase of property are subject to our terms and conditions.
                                In the event of a withdrawal by the buyer, 30% of the total purchase value shall be forfeited as liquidated damages. No refund will
                                be issued if the buyer's account is over 60 days in arrears. Any existing balance will only be refundable after a new buyer has been secured and full payment of the property has been received.
                                By making any payment, the buyer acknowledgesand agrees to these terms.[updated 2023]
                            </span>
                        </p>
                    </div>
                </div>
            )}
        </Layout>
    )
}

export default Receipt